/**
 * Orchestrates Configure SSO — protocol step (Step 3) with assist + agent paths.
 */
import type { WizardQuickOption } from './vysorWizardEngine';
import { advanceVysorWizard, type WizardState } from './vysorWizardEngine';
import type { VysorAppCatalogItem, VysorIdpOption } from './vysorCatalog';
import type { VysorWizardContext } from './vysorWizardEngine';
import {
  buildWizardContextForProtocolAssist,
  inferSsoProtocolFromText,
} from './vysorSsoProtocolAssist';
import {
  resolveReferenceText,
  shouldTryReferenceInference,
} from './vysorWizardAssistCommon';
import { fetchUrlContentForVysor } from './vysorMetadata';

export type SsoProtocolStepOutcome =
  | { kind: 'pass' }
  | { kind: 'inferred'; protocol: 'SAML' | 'OIDC'; summary: string; referenceNote: string }
  | { kind: 'assist'; message: string; options: WizardQuickOption[] }
  | { kind: 'agent'; message: string; attachmentText?: string }
  | { kind: 'invalid'; hint: string };

export function buildSsoProtocolAssistOptions(): WizardQuickOption[] {
  return [
    { id: 'sso_saml', label: 'SAML', value: 'SAML' },
    { id: 'sso_oidc', label: 'OIDC', value: 'OIDC' },
    { id: 'wz_not_sure', label: 'Not sure', value: 'Not sure' },
    { id: 'wz_cancel', label: 'Cancel', value: '__cancel__' },
  ];
}

function chipProtocolPick(optionValue?: string): 'SAML' | 'OIDC' | 'Not sure' | null {
  if (optionValue === 'SAML' || optionValue === 'OIDC' || optionValue === 'Not sure') return optionValue;
  return null;
}

export async function resolveSsoProtocolStepInput(
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  collected: Record<string, string>,
  _assistActive: boolean,
): Promise<SsoProtocolStepOutcome> {
  const trimmed = rawAnswer.trim();
  const chip = chipProtocolPick(optionValue);

  if (chip === 'SAML' || chip === 'OIDC') {
    return { kind: 'pass' };
  }

  // Typed exact protocol
  if (/^(saml|saml2)$/i.test(trimmed)) return { kind: 'pass' };
  if (/^(oidc|openid)$/i.test(trimmed)) return { kind: 'pass' };

  // Any other free text / Not sure → model answers THIS message dynamically
  if (chip === 'Not sure' || trimmed) {
    if (shouldTryReferenceInference(trimmed)) {
      try {
        const { text, source } = await resolveReferenceText(trimmed, async url => {
          const fetched = await fetchUrlContentForVysor(url);
          return { text: fetched.text };
        });
        const inferred = inferSsoProtocolFromText(text);
        if (inferred && inferred.confidence !== 'low') {
          const refLabel = source === 'url' ? 'metadata URL' : 'reference you shared';
          return {
            kind: 'inferred',
            protocol: inferred.protocol,
            summary:
              `Based on the ${refLabel}, this looks like **${inferred.protocol}** — ${inferred.reason}`,
            referenceNote: trimmed.slice(0, 2000),
          };
        }
        return {
          kind: 'agent',
          message: buildWizardContextForProtocolAssist(
            collected,
            trimmed,
            inferred
              ? `Possible ${inferred.protocol} hint: ${inferred.reason}\n\n${text.slice(0, 6000)}`
              : text.slice(0, 8000),
          ),
          attachmentText: text.slice(0, 8000),
        };
      } catch {
        /* fall through to agent */
      }
    }

    return {
      kind: 'agent',
      message: buildWizardContextForProtocolAssist(
        collected,
        trimmed || 'I am not sure which protocol to use.',
      ),
    };
  }

  return {
    kind: 'invalid',
    hint: 'Choose **SAML** or **OIDC**, or ask anything about this step in your own words.',
  };
}

export function applyInferredProtocolToWizard(
  wizard: WizardState,
  protocol: 'SAML' | 'OIDC',
  _catalog: VysorAppCatalogItem[],
  _context: VysorWizardContext,
  referenceNote?: string,
  metadataExtras?: Record<string, string>,
) {
  const collected = {
    ...wizard.collected,
    ...(metadataExtras || {}),
    sso_type: protocol,
    sso_protocol_reference: referenceNote || wizard.collected.sso_protocol_reference || '',
  };
  const nextIndex = wizard.stepIndex + 1;
  const nextField = wizard.flow.fields[nextIndex];
  return {
    wizard: { ...wizard, stepIndex: nextIndex, collected, ssoProtocolAssistActive: false, assistFieldKey: undefined },
    nextField,
    collected,
  };
}

/** After agent chat returns sso_type in prefill, resume wizard at IdP step. */
export function applyAgentProtocolHint(
  wizard: WizardState,
  ssoType: 'SAML' | 'OIDC',
): WizardState | null {
  if (wizard.flow.id !== 'configure-sso') return null;
  const field = wizard.flow.fields[wizard.stepIndex];
  if (field?.key !== 'sso_type') return null;
  const nextIndex = wizard.stepIndex + 1;
  if (nextIndex >= wizard.flow.fields.length) return null;
  return {
    ...wizard,
    stepIndex: nextIndex,
    ssoProtocolAssistActive: false,
    assistFieldKey: undefined,
    collected: { ...wizard.collected, sso_type: ssoType },
  };
}

export function tryAdvanceProtocolPick(
  wizard: WizardState,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext,
) {
  return advanceVysorWizard(wizard, rawAnswer, optionId, optionValue, catalog, context);
}
